import { auth } from "./firebase.js";
import { onAuthStateChanged } from "https://www.gstatic.com/firebasejs/10.13.2/firebase-auth.js";
import { api, ApiError } from "./api.js";
import { startRouter, navigate } from "./router.js";
import { initAuthUI, resetSignInForm } from "./auth.js";
import { loadMe, renderOnboarding } from "./onboarding.js";
import { initLocale, adoptAccountLocale, releaseAccount, wireLanguageSwitches } from "./locale.js";

const screenSignin = document.getElementById("screen-signin");
const screenShell = document.getElementById("screen-shell");
const screenOnboarding = document.getElementById("screen-onboarding");
const viewEl = document.getElementById("view");
const newTripBtn = document.getElementById("new-trip-btn");
const appSidebar = document.getElementById("app-sidebar");
const sidebarMenuBtn = document.getElementById("sidebar-menu-btn");
const sidebarBackdrop = document.getElementById("sidebar-backdrop");
const shell = window.TransportOSShell;

const { onboardingState } = window.TransportOSOnboarding;
let authSession = 0;
let sidebarUser = { me: null, phone: null };

function showSignedOut() {
  screenShell.hidden = true;
  screenOnboarding.hidden = true;
  screenSignin.hidden = false;
  resetSignInForm();
}

function showScreen(screen) {
  [screenSignin, screenOnboarding, screenShell].forEach((el) => {
    el.hidden = el !== screen;
  });
}

function setBadge(name, text) {
  const el = document.querySelector(`[data-badge="${name}"]`);
  if (!el) return;
  el.textContent = text || "";
  el.hidden = !text;
}

function renderSidebarUser(me, fallbackPhone) {
  sidebarUser = { me, phone: fallbackPhone || sidebarUser.phone };
  const chip = shell.userChip(me, fallbackPhone);
  document.getElementById("sidebar-user-initials").textContent = chip.initials;
  document.getElementById("sidebar-user-label").textContent = chip.label;
}

async function optional(path) {
  try {
    return await api.get(path);
  } catch {
    return null;
  }
}

async function refreshSidebar() {
  const [settings, inTransit, documents, loans] = await Promise.all([
    optional("/settings"),
    optional("/trips?status=in_transit&limit=100"),
    optional("/documents/summary"),
    optional("/loans/summary"),
  ]);
  const org = shell.orgCard(settings);
  document.getElementById("org-card-name").textContent = org.name;
  document.getElementById("org-card-meta").textContent = org.meta;
  setBadge("vehicles", settings ? shell.countBadge(settings.vehicleCount) : null);
  setBadge("trips", shell.listCountBadge(inTransit));
  setBadge("documents", shell.expiryBadge(documents));
  setBadge("loans", shell.loansOverdueBadge(loans));
}

function openNewTrip() {
  navigate("/trips");
  const deadline = Date.now() + 5000;
  const tryOpen = () => {
    const addBtn = viewEl.querySelector('[data-role="add"]');
    if (addBtn) {
      addBtn.click();
    } else if (Date.now() < deadline) {
      setTimeout(tryOpen, 50);
    }
  };
  tryOpen();
}

function focusableIn(el) {
  return [...el.querySelectorAll("button, [href], input, select, textarea")].filter((node) => !node.disabled && node.offsetParent !== null);
}

let drawerReturnFocus = null;

function openSidebarDrawer() {
  drawerReturnFocus = sidebarMenuBtn;
  appSidebar.classList.add("is-open");
  sidebarBackdrop.hidden = false;
  document.body.classList.add("scroll-locked");
  sidebarMenuBtn.setAttribute("aria-expanded", "true");
  sidebarMenuBtn.setAttribute("aria-label", window.TransportOSI18n.t("shell.mobileMenu.close"));
  const first = focusableIn(appSidebar)[0];
  if (first) first.focus();
}

function closeSidebarDrawer() {
  if (!appSidebar.classList.contains("is-open")) return;
  appSidebar.classList.remove("is-open");
  sidebarBackdrop.hidden = true;
  document.body.classList.remove("scroll-locked");
  sidebarMenuBtn.setAttribute("aria-expanded", "false");
  sidebarMenuBtn.setAttribute("aria-label", window.TransportOSI18n.t("shell.mobileMenu.open"));
  const back = drawerReturnFocus;
  drawerReturnFocus = null;
  if (back) back.focus();
}

function initSidebarDrawer() {
  sidebarMenuBtn.addEventListener("click", () => {
    if (appSidebar.classList.contains("is-open")) closeSidebarDrawer();
    else openSidebarDrawer();
  });
  sidebarBackdrop.addEventListener("click", closeSidebarDrawer);
  // Any link or the New Trip button inside the drawer starts a navigation; close rather
  // than leave the drawer open behind the page it just left.
  appSidebar.addEventListener("click", (e) => {
    if (e.target.closest("a[data-link], #new-trip-btn")) closeSidebarDrawer();
  });
  window.addEventListener("popstate", closeSidebarDrawer);
  document.addEventListener("keydown", (e) => {
    if (!appSidebar.classList.contains("is-open")) return;
    if (e.key === "Escape") {
      e.preventDefault();
      closeSidebarDrawer();
      return;
    }
    if (e.key !== "Tab") return;
    const items = focusableIn(appSidebar);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (!appSidebar.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });
}

async function renderBuildVersion() {
  let info = null;
  try {
    const res = await fetch("build.json", { cache: "no-store" });
    if (res.ok) info = await res.json();
  } catch {
    /* no stamp outside a deploy */
  }
  const label = window.TransportOSBuild.buildLabel(info);
  document.querySelectorAll("[data-build-version]").forEach((el) => {
    el.textContent = label.text;
    el.title = label.title;
  });
}

async function enterShell(me, phone, session) {
  await adoptAccountLocale(me, (locale) => api.patch("/me", { locale }));
  if (session !== authSession) return;
  showScreen(screenShell);
  renderSidebarUser(me, phone);
  startRouter(viewEl);
  refreshSidebar();
}

// Not a top-level await: the auth listener must be registered as soon as this module runs.
const localeReady = initLocale();
wireLanguageSwitches();

onAuthStateChanged(auth, async (user) => {
  const session = ++authSession;
  if (!user) {
    releaseAccount();
    showSignedOut();
    return;
  }
  await localeReady;

  let me = null;
  try {
    me = await loadMe();
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return;
    console.warn("Failed to load /me", err);
  }
  if (session !== authSession) return;

  if (onboardingState(me) === "choose") {
    showScreen(screenOnboarding);
    renderOnboarding(me, {
      onDone: (next) => {
        if (session === authSession) enterShell(next, user.phoneNumber, session);
      },
    });
    return;
  }
  await enterShell(me, user.phoneNumber, session);
});

newTripBtn.addEventListener("click", openNewTrip);
initSidebarDrawer();
// Waits for the locale bundle so the label never renders its raw i18n key before the dictionary
// that translates it has loaded; initLocale() itself never rejects.
localeReady.then(renderBuildVersion);
window.addEventListener("transportos:shell-refresh", () => {
  refreshSidebar();
  api.get("/me").then((me) => renderSidebarUser(me)).catch(() => {});
});
window.addEventListener("transportos:locale-change", () => {
  renderBuildVersion();
  if (screenShell.hidden) return;
  renderSidebarUser(sidebarUser.me, sidebarUser.phone);
  refreshSidebar();
});

initAuthUI();
